import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { Booking, BookingDocument, BookingTask, Invoice, ListResponse } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows, membershipAllowsIn } from "../../auth/permissions";
import { formatDateTime, formatMoney, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EditorPage,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { DetailPageSkeleton, SkeletonTable } from "../../ui/skeletons";
import type { AssetReservation, InventoryReservation } from "../inventory/inventoryTypes";
import { VendorServicesPanel } from "../vendors/VendorServicesPanel";

const statusTransitions: Record<string, string[]> = {
  draft: ["enquiry", "provisional", "cancelled"],
  enquiry: ["held", "provisional", "declined", "cancelled"],
  held: ["provisional", "confirmed", "expired", "cancelled"],
  provisional: ["pending_approval", "confirmed", "rejected", "cancelled"],
  pending_approval: ["confirmed", "rejected", "cancelled"],
  confirmed: ["in_progress", "completed", "cancelled"],
  in_progress: ["completed", "cancelled"],
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function BookingDetailPage() {
  const { bookingId = "" } = useParams<{ bookingId: string }>();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<unknown>();
  const [working, setWorking] = useState(false);
  const membership = auth.session?.selectedMembership;
  // A hire is lettings work from end to end, so every control here is judged by the lettings role
  // alone. Asking across both would offer somebody the buttons for a side of the building they hold
  // nothing in, and the server would refuse each one in turn.
  const canManage = membershipAllowsIn(membership, "bookings", "bookings.manage");
  const canManageTasks = membershipAllowsIn(membership, "bookings", "bookings.tasks.manage");
  const canViewFinance = membershipAllowsIn(membership, "bookings", "invoices.issue");
  // Stock is the client's own kit wherever it is going, so it is asked across both halves, which is
  // how the server judges the reservation routes too.
  const canViewInventory = membershipAllows(membership, "inventory.operate");
  // Hiring somebody in is the client committing their own money to a supplier, so the directory and
  // the engagements sit on the events side even when the work is being done at somebody else's
  // hire. Read with the events role or not at all: every request that panel makes would be refused
  // without one, and a refusal is not a panel.
  const canSeeHires = membershipAllowsIn(membership, "events", "calendar.view");
  const canHire = membershipAllowsIn(membership, "events", "vendors.manage");
  const booking = useQuery({
    queryKey: ["bookings", bookingId],
    queryFn: () => api<Booking>(`/api/v1/bookings/${bookingId}`),
    enabled: Boolean(bookingId),
  });
  const tasks = useQuery({
    queryKey: ["bookings", bookingId, "tasks"],
    queryFn: () => api<ListResponse<BookingTask>>(`/api/v1/bookings/${bookingId}/tasks`),
    enabled: Boolean(bookingId && canManageTasks),
  });
  const documents = useQuery({
    queryKey: ["bookings", bookingId, "documents"],
    queryFn: () => api<ListResponse<BookingDocument>>(`/api/v1/bookings/${bookingId}/documents`),
    enabled: Boolean(bookingId),
  });
  const invoices = useQuery({
    queryKey: ["invoices", "booking-overview", bookingId],
    queryFn: () =>
      api<ListResponse<Invoice>>(
        `/api/v1/invoices?bookingId=${encodeURIComponent(bookingId)}&limit=100&offset=0`,
      ),
    enabled: Boolean(bookingId && canViewFinance),
  });
  const reservations = useQuery({
    queryKey: ["inventory", "reservations", bookingId],
    queryFn: () =>
      api<ListResponse<InventoryReservation>>(
        `/api/v1/bookings/${bookingId}/inventory-reservations`,
      ),
    enabled: Boolean(bookingId && canViewInventory),
  });
  const assetReservations = useQuery({
    queryKey: ["inventory", "asset-reservations", bookingId],
    queryFn: () =>
      api<ListResponse<AssetReservation>>(`/api/v1/bookings/${bookingId}/asset-reservations`),
    enabled: Boolean(bookingId && canViewInventory),
  });
  const availableStatuses = booking.data ? (statusTransitions[booking.data.status] ?? []) : [];

  async function transition(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!booking.data || !bookingId || !canManage) return;
    const formElement = event.currentTarget;
    const data = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/bookings/${bookingId}/transition`, {
        method: "POST",
        body: jsonBody({
          status: String(data.get("status")),
          reason: String(data.get("reason")),
          category: "staff_decision",
          version: booking.data.version,
        }),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bookings"] }),
        booking.refetch(),
      ]);
      formElement.reset();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function addTask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!bookingId || !canManageTasks) return;
    const formElement = event.currentTarget;
    const data = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/bookings/${bookingId}/tasks`, {
        method: "POST",
        body: jsonBody({
          title: String(data.get("title")),
          category: String(data.get("category")),
          assignedRole: String(data.get("assignedRole")),
          assignedUserId: null,
          dueAt: data.get("dueAt") ? new Date(String(data.get("dueAt"))).toISOString() : null,
        }),
      });
      await tasks.refetch();
      formElement.reset();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function updateTask(task: BookingTask, status: string) {
    if (!bookingId || !canManageTasks) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/bookings/${bookingId}/tasks/${task.id}/status`, {
        method: "PUT",
        body: jsonBody({ status }),
      });
      await tasks.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function uploadDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!bookingId || !canManage) return;
    const formElement = event.currentTarget;
    const data = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api<BookingDocument>(`/api/v1/bookings/${bookingId}/documents`, {
        method: "POST",
        body: data,
      });
      await documents.refetch();
      formElement.reset();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function updateDocumentVisibility(
    document: BookingDocument,
    visibility: "staff" | "customer",
  ) {
    if (!bookingId || !canManage || visibility === document.visibility) return;
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/bookings/${bookingId}/documents/${document.id}/visibility`, {
        method: "PUT",
        body: jsonBody({ visibility, rowVersion: document.rowVersion }),
      });
      await documents.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  const bookingInvoices = (invoices.data?.items ?? []).filter(
    (invoice) => invoice.bookingId === bookingId,
  );
  const outstandingMinor = bookingInvoices.reduce(
    (total, invoice) => total + invoice.balanceMinor,
    0,
  );
  const resourceReservations =
    (reservations.data?.items ?? []).filter((item) => item.status !== "cancelled").length +
    (assetReservations.data?.items ?? []).filter((item) => item.status !== "cancelled").length;
  const openTasks = (tasks.data?.items ?? []).filter(
    (task) => task.status !== "completed" && task.status !== "cancelled",
  );
  const blockedTasks = openTasks.filter((task) => task.status === "blocked").length;
  const allocations =
    booking.data?.occurrences?.flatMap((occurrence) => occurrence.allocations) ?? [];
  const places = [
    ...new Set(
      allocations.map((allocation) => `${allocation.venueName} · ${allocation.locationName}`),
    ),
  ];

  return (
    <EditorPage
      title={booking.data?.title ?? "Booking overview"}
      description={
        booking.data
          ? `${booking.data.reference} · ${booking.data.customerName}`
          : "Loading booking record"
      }
      backTo="/calendar"
      backLabel="Back to calendar"
    >
      {booking.isLoading ? (
        <DetailPageSkeleton title="Booking" backTo="/calendar" backLabel="Back to calendar" />
      ) : booking.error ? (
        <ErrorPanel error={booking.error} />
      ) : booking.data ? (
        <div className="stack">
          {error ? <ErrorPanel error={error} /> : null}
          {booking.data.occurrences?.some((occurrence) =>
            occurrence.allocations.some((allocation) => allocation.overridden),
          ) ? (
            <Notice tone="warning" title="Conflict override present">
              At least one allocation was intentionally overridden. The audit record preserves who
              approved it and why.
            </Notice>
          ) : null}
          <Card className="event-overview-hero">
            <CardHeader
              title="Booking overview"
              description="The shared brief for the venue team, operations, finance, and everybody else authorized to work on this rental."
              actions={<StatusPill value={booking.data.status} />}
            />
            <div className="event-summary-strip">
              <div>
                <span>When</span>
                <strong>{formatDateTime(booking.data.start, booking.data.timezone)}</strong>
                <small>to {formatDateTime(booking.data.end, booking.data.timezone)}</small>
              </div>
              <div>
                <span>Where</span>
                <strong>{places.join(", ") || "Location being finalized"}</strong>
                <small>{booking.data.timezone}</small>
              </div>
              <div>
                <span>Customer</span>
                <strong>{booking.data.customerName}</strong>
                <small>{titleCase(booking.data.source)} source</small>
              </div>
              <div>
                <span>Attendance</span>
                <strong>{booking.data.attendeeCount ?? "Pending"}</strong>
                <small>Expected guests</small>
              </div>
            </div>
            <div className="event-brief-grid">
              <div>
                <h3>Booking brief</h3>
                <p>{booking.data.description || "No booking brief has been added yet."}</p>
              </div>
              <div>
                <h3>Customer notes</h3>
                <p>{booking.data.customerNotes || "No customer notes have been added."}</p>
              </div>
              <div>
                <h3>Internal notes</h3>
                <p>{booking.data.internalNotes || "No internal notes have been added."}</p>
              </div>
            </div>
          </Card>

          <section aria-labelledby="booking-readiness-heading">
            <div className="section-heading-row compact">
              <div>
                <span className="eyebrow">Operational picture</span>
                <h2 id="booking-readiness-heading">Booking readiness</h2>
              </div>
            </div>
            <div className="event-overview-grid">
              {canManageTasks ? (
                <Card>
                  <CardHeader title="Team" description="Assigned work and blockers." />
                  <strong className="overview-value">
                    {tasks.isLoading
                      ? "Loading"
                      : tasks.error
                        ? "Unavailable"
                        : `${openTasks.length} open`}
                  </strong>
                  <p>
                    {blockedTasks
                      ? `${blockedTasks} blocked task${blockedTasks === 1 ? "" : "s"}`
                      : "No blocked tasks"}
                  </p>
                </Card>
              ) : null}
              {canViewFinance ? (
                <Card>
                  <CardHeader title="Commercial" description="Invoices and outstanding balance." />
                  <strong className="overview-value">
                    {invoices.isLoading
                      ? "Loading"
                      : invoices.error
                        ? "Unavailable"
                        : formatMoney(outstandingMinor, bookingInvoices[0]?.currency ?? "USD")}
                  </strong>
                  <p>
                    {bookingInvoices.length} invoice{bookingInvoices.length === 1 ? "" : "s"}
                  </p>
                  <Link to="/finance">Open finance</Link>
                </Card>
              ) : null}
              {canViewInventory ? (
                <Card>
                  <CardHeader title="Resources" description="Stock and tracked assets reserved." />
                  <strong className="overview-value">
                    {reservations.isLoading || assetReservations.isLoading
                      ? "Loading"
                      : reservations.error || assetReservations.error
                        ? "Unavailable"
                        : resourceReservations}
                  </strong>
                  <p>Active resource reservation{resourceReservations === 1 ? "" : "s"}</p>
                  <Link to="/inventory">Open inventory</Link>
                </Card>
              ) : null}
            </div>
          </section>

          <div className="content-grid">
            <Card className="span-7">
              <CardHeader
                title="Schedule summary"
                actions={<StatusPill value={booking.data.status} />}
              />
              <dl className="definition-list">
                <dt>Customer</dt>
                <dd>{booking.data.customerName}</dd>
                <dt>Starts</dt>
                <dd>{formatDateTime(booking.data.start, booking.data.timezone)}</dd>
                <dt>Ends</dt>
                <dd>{formatDateTime(booking.data.end, booking.data.timezone)}</dd>
                <dt>Attendees</dt>
                <dd>{booking.data.attendeeCount ?? "Not available"}</dd>
                <dt>Source</dt>
                <dd>{titleCase(booking.data.source)}</dd>
              </dl>
            </Card>
            <Card className="span-5">
              <CardHeader
                title="Manage lifecycle"
                description="Move the booking through valid, audited lifecycle stages."
              />
              {canManage && availableStatuses.length ? (
                <form className="form-stack" onSubmit={transition}>
                  <Field label="Next status">
                    <select name="status" required defaultValue="">
                      <option value="" disabled>
                        Select status
                      </option>
                      {availableStatuses.map((status) => (
                        <option value={status} key={status}>
                          {titleCase(status)}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Reason">
                    <textarea name="reason" required minLength={3} />
                  </Field>
                  <FormActions submitting={working} submitLabel="Update status" />
                </form>
              ) : canManage ? (
                <Notice tone="info">
                  This booking is in a terminal state and has no further lifecycle actions.
                </Notice>
              ) : (
                <Notice tone="info">
                  Your role can view this booking but cannot change its lifecycle.
                </Notice>
              )}
            </Card>
          </div>
          <Card>
            <CardHeader
              title="Schedule and run of show"
              description="Every occurrence and the spaces it holds."
            />
            {booking.data.occurrences?.map((occurrence) => (
              <div className="timeline-item" key={occurrence.id}>
                <strong>{formatDateTime(occurrence.start, booking.data?.timezone)}</strong>
                <p>
                  {occurrence.allocations
                    .map((allocation) => `${allocation.venueName} · ${allocation.locationName}`)
                    .join(", ")}
                </p>
              </div>
            ))}
          </Card>
          {/* What the client hired in for somebody else's hire. Billed from here it lands on the
              hirer's invoice, which is the difference between this and the same panel on an event:
              there the cost stays with the client because the event is theirs. The directory itself
              belongs to the events side, so somebody who only works the lettings is not shown a
              panel whose every request would be refused. */}
          {canSeeHires ? (
            <VendorServicesPanel
              owner={{
                kind: "booking",
                id: booking.data.id,
                reference: booking.data.reference,
                customerId: booking.data.customerId,
              }}
              canManage={canHire}
              canBill={canViewFinance}
            />
          ) : null}
          <Card>
            <CardHeader
              title="Documents"
              description="Every upload is size-limited, type-checked, malware-scanned, private by default, and downloadable only through an authorized request."
            />
            {documents.isLoading ? (
              <SkeletonTable bare columns={4} rows={3} />
            ) : documents.error ? (
              <ErrorPanel error={documents.error} />
            ) : documents.data?.items.length ? (
              <DataTable
                caption="Booking documents"
                items={documents.data.items}
                getKey={(document) => document.id}
                columns={[
                  {
                    label: "Document",
                    render: (document) => (
                      <>
                        <strong>{document.filename}</strong>
                        <br />
                        <small>
                          {titleCase(document.type)} · v{document.version} ·{" "}
                          {formatFileSize(document.sizeBytes)}
                        </small>
                      </>
                    ),
                  },
                  {
                    label: "Scan",
                    render: (document) => <StatusPill value={document.scanStatus} />,
                  },
                  {
                    label: "Visibility",
                    render: (document) =>
                      canManage ? (
                        <select
                          aria-label={`Visibility for ${document.filename}`}
                          value={document.visibility}
                          disabled={working}
                          onChange={(event) =>
                            updateDocumentVisibility(
                              document,
                              event.target.value as "staff" | "customer",
                            )
                          }
                        >
                          <option value="staff">Staff only</option>
                          <option value="customer">Customer portal</option>
                        </select>
                      ) : (
                        titleCase(document.visibility)
                      ),
                  },
                  {
                    label: "Actions",
                    render: (document) => (
                      <a
                        className="button secondary small"
                        href={`/api/v1/bookings/${bookingId}/documents/${document.id}/download`}
                        download
                      >
                        Download
                      </a>
                    ),
                  },
                ]}
              />
            ) : (
              <p className="muted">No documents have been attached.</p>
            )}
            {canManage ? (
              <form className="form-grid" onSubmit={uploadDocument}>
                <Field label="Document type">
                  <select name="type" defaultValue="other">
                    <option value="contract">Contract</option>
                    <option value="quote">Quote</option>
                    <option value="invoice">Invoice</option>
                    <option value="risk_assessment">Risk assessment</option>
                    <option value="floor_plan">Floor plan</option>
                    <option value="customer_upload">Customer upload</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field
                  label="Visibility"
                  hint="Staff only is the safe default. Customer portal files become visible immediately after upload."
                >
                  <select name="visibility" defaultValue="staff">
                    <option value="staff">Staff only</option>
                    <option value="customer">Customer portal</option>
                  </select>
                </Field>
                <Field label="File" hint="PDF, JPEG, PNG, WebP, text, CSV, DOCX, XLSX, or PPTX.">
                  <input
                    name="document"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.txt,.csv,.docx,.xlsx,.pptx"
                    required
                  />
                </Field>
                <div className="full">
                  <FormActions submitting={working} submitLabel="Scan and upload" />
                </div>
              </form>
            ) : null}
          </Card>
          {canManageTasks ? (
            <Card>
              <CardHeader
                title="Team readiness"
                description="Assign clear actions to a fixed staff role. Operations staff can update tasks without gaining wider booking powers."
              />
              {tasks.isLoading ? (
                <SkeletonTable bare columns={4} rows={3} />
              ) : tasks.error ? (
                <ErrorPanel error={tasks.error} />
              ) : tasks.data?.items.length ? (
                <DataTable
                  caption="Booking tasks"
                  items={tasks.data.items}
                  getKey={(task) => task.id}
                  columns={[
                    {
                      label: "Task",
                      render: (task) => (
                        <>
                          <strong>{task.title}</strong>
                          <br />
                          <small>{titleCase(task.category)}</small>
                        </>
                      ),
                    },
                    { label: "Assigned", render: (task) => titleCase(task.assignedRole) },
                    { label: "Due", render: (task) => formatDateTime(task.dueAt) },
                    {
                      label: "Status",
                      render: (task) => (
                        <select
                          aria-label={`Status for ${task.title}`}
                          value={task.status}
                          disabled={working}
                          onChange={(event) => updateTask(task, event.target.value)}
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In progress</option>
                          <option value="blocked">Blocked</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Canceled</option>
                        </select>
                      ),
                    },
                  ]}
                />
              ) : (
                <p className="muted">No tasks have been added.</p>
              )}
              <form className="form-grid" onSubmit={addTask}>
                <Field label="Task">
                  <input name="title" required />
                </Field>
                <Field label="Category">
                  <input name="category" defaultValue="general" required />
                </Field>
                <Field label="Assigned role">
                  <select name="assignedRole" defaultValue="operations">
                    <option value="operations">Operations</option>
                    <option value="booking_coordinator">Booking coordinator</option>
                    <option value="venue_manager">Venue manager</option>
                    <option value="finance">Finance</option>
                  </select>
                </Field>
                <Field label="Due">
                  <input name="dueAt" type="datetime-local" />
                </Field>
                <div className="full">
                  <FormActions submitting={working} submitLabel="Add task" />
                </div>
              </form>
            </Card>
          ) : null}
        </div>
      ) : null}
    </EditorPage>
  );
}
